import { Controller } from "@hotwired/stimulus"
import { setPreset, presets } from "../shared/live_helper";

export default class extends Controller {
  static targets = ['cameraSelection', 'presetSelect', 'imageView', 'isStream',
                    'isHdStream', 'presetIndex', 'isDirectSnapshot', 'hdStreamViewer',
                    'companySlug']

  connect() {
    const self = this
    this.timerId = null;
    this.isDisconnected = false;

    const preset = presets()[this.presetIndexTarget.value]
    this.cameraSelectionTarget.value = preset?.cameraId || ''
    this.isStreamTarget.checked = preset?.isStream || false

    this.cameraSelectionTarget.addEventListener('change', () => {
      setPreset(self.presetIndexTarget.value, self.cameraSelectionTarget.value, self.isStreamTarget.checked)
      self.addLoading();
      self.initPtz();
      self.runLiveView();
    })
    this.isStreamTarget.addEventListener('change', () => {
      self.isHdStreamTarget.checked = false
      setPreset(self.presetIndexTarget.value, self.cameraSelectionTarget.value, self.isStreamTarget.checked)
      self.addLoading();
      self.runLiveView();
    })

    this.isHdStreamTarget.addEventListener('change', () => {
      self.isStreamTarget.checked = false
      self.addLoading();
      self.runLiveView();
    })
    this.cameraSelectionTarget.dispatchEvent(new Event('change'));

    this.hdStreamViewerTarget.controls    = false;
    this.hdStreamViewerTarget.muted       = true;
    this.hdStreamViewerTarget.autoplay    = true;
    this.hdStreamViewerTarget.playsInline = true;

    // Enter full screen for hd video
    this.hdStreamViewerTarget.addEventListener('dblclick', () => self.hdStreamFullscreen());

    let lastTap = 0;
    this.hdStreamViewerTarget.addEventListener('touchend', (e) => {
      const now = Date.now();
      if (now - lastTap < 300) self.hdStreamFullscreen();
      lastTap = now;
    });

    // this.hdStreamViewerTarget.addEventListener("webkitbeginfullscreen", () => {
    //   console.log("Video entered fullscreen on iOS Safari");
    // });

    // Fires when the video exits fullscreen
    this.hdStreamViewerTarget.addEventListener("webkitendfullscreen", () => {
      setTimeout(() => self.hdStreamViewerTarget.play(), 500);
    });

    // controls="" autoplay="" playsinline=""
  }

  hdStreamFullscreen() {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else if (this.hdStreamViewerTarget.requestFullscreen) {
      this.hdStreamViewerTarget.requestFullscreen();
    } else if (this.hdStreamViewerTarget.webkitRequestFullscreen) { /* Safari */
      this.hdStreamViewerTarget.webkitRequestFullscreen();
    } else if (this.hdStreamViewerTarget.msRequestFullscreen) { /* IE11 */
      this.hdStreamViewerTarget.msRequestFullscreen();
    } else if (this.hdStreamViewerTarget.webkitEnterFullscreen) {
      try { this.hdStreamViewerTarget.webkitEnterFullscreen(); } catch {}
    }

    this.hdStreamViewerTarget.play();
  }

  addLoading() {
    this.element.classList.add('live-layout__loading')
  }

  changeZoom(e) {
    this.imageViewTarget.dataset.hideZoom = !e.currentTarget.checked
  }

  removeLoading() {
    this.element.classList.remove('live-layout__loading')
  }

  disconnect() {
    this.isDisconnected = true;
    this.resetImage();
  }

  resetImage() {
    this.imageViewTarget.src = ''
    // Reset callbacks
    this.imageViewTarget.onload  = null;
    this.imageViewTarget.onerror = null;
    // this.imageViewTarget.removeAttribute('src')
    clearTimeout(this.timerId);
    this.addLoading()
  }

  runLiveView() {
    this.resetImage()

    if (this.isHdStreamTarget.checked) {
      this.imageViewTarget.style.display = 'none'
      this.hdStreamViewerTarget.style.display = 'block'
    } else {
      this.imageViewTarget.style.display = 'block'
      this.hdStreamViewerTarget.style.display = 'none'
      this.hdStreamViewerTarget.src = ''
    }

    if (this.isStreamTarget.checked) {
      this.runStreamLiveView()
    } else if (this.isHdStreamTarget.checked) {
      this.runHDStreamLiveView()
    } else {
      this.runSnapshotLiveView()
    }
  }

  runHDStreamLiveView() {
    const self = this;
    clearInterval(this.timerId)
    // this.hdStreamViewerTarget.src = 'http://192.168.31.134:10001/camera6'
    const src = `https://${window.location.host}/hd_stream/${this.companySlugTarget.value}_camera_${this.cameraSelectionTarget.value}/`
    // const src = 'http://192.168.31.134:10001/camera6/'

    new MediaMTXWebRTCReader({
      url: new URL('whep', src),
      onError: (err) => {
        self.addLoading();
      },
      onTrack: (evt) => {
        self.removeLoading();
        self.hdStreamViewerTarget.srcObject = evt.streams[0];
      },
    });
  }

  runStreamLiveView() {
    if (!this.cameraSelectionTarget.value) return
    if (this.isDisconnected) return
    if (this.isHdStreamTarget.checked) return

    self = this
    this.imageViewTarget.src = Routes.liveStream(this.cameraSelectionTarget.value) // 'http://192.168.31.58:81/stream'
    // this.imageViewTarget.src = 'http://192.168.31.58:81/stream'

    this.imageViewTarget.onload = () => {
      this.removeLoading();
    }

    this.imageViewTarget.onerror = () => {
      self.resetImage();
      self.timerId = setTimeout(() => self.runLiveView(), 1500)
    }

    this.checkStreamIsWorking()
  }

  checkStreamIsWorking() {
    const self = this
    this.timerId = setTimeout(() => {
      if (self.isDisconnected) return
      if (this.isHdStreamTarget.checked) { return }

      if (self.imageViewTarget.naturalWidth == 0 || self.imageViewTarget.naturalHeight == 0) {
        self.runLiveView()
      } else {
        self.removeLoading();
        self.checkStreamIsWorking()
      }
    }, 1000)
  }

  runSnapshotLiveView(delay = 100) {
    if (this.isDisconnected) { return }
    if (this.isHdStreamTarget.checked) { return }

    const self = this;

    this.timerId = setTimeout(() => {
      if (!this.cameraSelectionTarget.value) return
      const params = self.imageViewTarget.width && self.imageViewTarget.height ? { w: self.imageViewTarget.width, h: self.imageViewTarget.height } : {}

      const url = this.isDirectSnapshot() ?
                    Routes.liveDirectSnapshot(this.cameraSelectionTarget.value, params) :
                    Routes.liveSnapshot(this.cameraSelectionTarget.value, params)

      Api.toDataURL(url, (dataUrl) => {
        if (this.isStreamTarget.checked) return
        if (this.cameraSelectionTarget.value == '') return

        this.removeLoading();
        this.imageViewTarget.src = dataUrl
        this.runSnapshotLiveView();
      }, (xhr) => {
        this.addLoading();

        // if (xhr.status == 404) {
        //   self.setState({ errors: ['Lost connection to the camera'] })
        // } else {
        //   self.setState({ errors: ['Can\'t connect to the camera'] })
        // }

        self.runSnapshotLiveView(1500)
      })

    // TODO: Make less delay for local? Or change delay according to connection
    }, delay)
  }

  initPtz() {
    const selectedCamera = this.cameraSelectionTarget.selectedOptions[0];
    if (selectedCamera && selectedCamera.dataset.ptz == 'true') {
      this.element.classList.add('ptz')
      this.fetchPtzPresets()
    } else {
      this.element.classList.remove('ptz')
    }
  }

  isDirectSnapshot() {
    const selectedCamera = this.cameraSelectionTarget.selectedOptions[0];
    if (selectedCamera && selectedCamera.dataset.isDirectSnapshot == 'true') return true
    return false
  }

  fetchPtzPresets() {
    const cameraId = this.cameraSelectionTarget.value

    Api.post(Routes.cameraPtz(cameraId, 'presets'), {}, (presets) => {
      // Ignore stale responses after the camera was switched
      if (this.cameraSelectionTarget.value != cameraId) return

      this.presetSelectTarget.querySelectorAll('option:not([value=""])').forEach((option) => option.remove())
      presets.forEach(({ id, name, selected }) => {
        const option = document.createElement('option');

        option.textContent = name;
        option.value = id;
        option.selected = selected
        this.presetSelectTarget.appendChild(option);
      });
    })
  }

  sendPtzAction(e) {
    const action = e.target.dataset.ptzAction
    let data = {
      ptz_params: {}
    }

    if (action == 'set_preset') {
      data.ptz_params.preset_id = e.target.selectedOptions[0].value
      if (!data.ptz_params.preset_id) return
    }

    Api.post(Routes.cameraPtz(this.cameraSelectionTarget.value, action), data)
  }
}
